import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, Card, CardContent, Stack, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductQuery,
} from 'state/api';
import { PageContainer } from 'components/shared';
import AddNewProduct from './addnew';
import GeneralSetup from './generalsetup';
import PricingAndOthers from './pricingandother';
import ProductImages from './productthumbnail';

const emptyForm = () => ({
  productCode: `PRD-${Date.now()}`,
  name: '',
  description: '',
  category: '',
  subCategory: '',
  brand: '',
  purchasePrice: '',
  sellingPrice: '',
  stock: '',
  minStock: 10,
  unit: 'pieces',
  barcode: '',
  tax: '',
  status: 'active',
  tags: [],
  images: [],
});

const ProductManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');
  const isEdit = Boolean(productId);

  const [createProduct, { isLoading: creating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
  const { data: productResp } = useGetProductQuery(productId, { skip: !isEdit });

  const [form, setForm] = useState(emptyForm);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  // Prefill the form when editing an existing product.
  useEffect(() => {
    const product = productResp?.data ?? productResp;
    if (isEdit && product && product._id) {
      setForm({
        productCode: product.productCode || '',
        name: product.name || '',
        description: product.description || '',
        category: product.category?.name || product.category || '',
        subCategory: product.subCategory || '',
        brand: product.brand?.name || product.brand || '',
        purchasePrice: product.purchasePrice ?? '',
        sellingPrice: product.sellingPrice ?? '',
        stock: product.stock ?? '',
        minStock: product.minStock ?? 10,
        unit: product.unit || 'pieces',
        barcode: product.barcode || '',
        tax: product.tax ?? '',
        status: product.status || 'active',
        tags: Array.isArray(product.tags) ? product.tags : [],
        images: Array.isArray(product.images) ? product.images : [],
      });
    }
  }, [isEdit, productResp]);

  const isLoading = creating || updating;

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const showError = (message) =>
    setSnackbar({ open: true, severity: 'error', message });

  const handleSubmit = async () => {
    const name = (form.name || '').trim();
    const productCode = (form.productCode || '').trim();
    const purchasePrice = Number(form.purchasePrice);
    const sellingPrice = Number(form.sellingPrice);
    const stock = Number(form.stock);

    // ── Client-side validation (mirrors backend contract) ────────────────
    if (productCode.length < 3 || productCode.length > 50) {
      return showError('Product code must be between 3 and 50 characters.');
    }
    if (name.length < 3 || name.length > 100) {
      return showError('Product name must be between 3 and 100 characters.');
    }
    if (!form.category) {
      return showError('Please select a category.');
    }
    if (form.purchasePrice === '' || Number.isNaN(purchasePrice) || purchasePrice < 0) {
      return showError('Purchase price must be a number greater than or equal to 0.');
    }
    if (form.sellingPrice === '' || Number.isNaN(sellingPrice) || sellingPrice < 0) {
      return showError('Selling price must be a number greater than or equal to 0.');
    }
    if (sellingPrice < purchasePrice) {
      return showError('Selling price must be greater than or equal to purchase price.');
    }
    if (form.stock === '' || !Number.isInteger(stock) || stock < 0) {
      return showError('Stock must be a whole number greater than or equal to 0.');
    }

    // ── Build payload (required fields + provided optionals) ─────────────
    const payload = {
      productCode,
      name,
      category: form.category,
      purchasePrice,
      sellingPrice,
      stock,
      minStock: Number(form.minStock) || 10,
      unit: form.unit,
      status: form.status,
    };

    const description = (form.description || '').trim();
    if (description && description !== '<p><br></p>') payload.description = description;
    if (form.subCategory) payload.subCategory = form.subCategory;
    if (form.brand) payload.brand = form.brand;
    if (form.barcode) payload.barcode = form.barcode.trim();
    if (form.tax !== '' && form.tax !== null && !Number.isNaN(Number(form.tax))) {
      payload.tax = Number(form.tax);
    }
    // tags & images are arrays on the product schema; always send them so that
    // removals persist on edit and new entries are saved on create.
    payload.tags = Array.isArray(form.tags) ? form.tags : [];
    payload.images = Array.isArray(form.images) ? form.images : [];

    try {
      if (isEdit) {
        await updateProduct({ id: productId, ...payload }).unwrap();
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Product updated successfully!',
        });
      } else {
        await createProduct(payload).unwrap();
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Product created successfully!',
        });
      }
      setTimeout(() => navigate('/products/list'), 900);
    } catch (err) {
      const message =
        err?.data?.error?.message ||
        err?.data?.message ||
        `Failed to ${isEdit ? 'update' : 'create'} product. Please try again.`;
      showError(message);
    }
  };

  return (
    <PageContainer
      title={isEdit ? 'Edit Product' : 'Add Product'}
      subtitle="Fill in the details below to save this product to your catalogue"
      actions={
        <Button
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Saving…' : isEdit ? 'Update Product' : 'Save Product'}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <AddNewProduct form={form} update={update} />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <GeneralSetup form={form} update={update} />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <PricingAndOthers form={form} update={update} />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <ProductImages form={form} update={update} />
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default ProductManagement;
